import type { Cart, CartProduct } from '../../graphql/codegen';
import type { FC, PropsWithChildren } from 'react';

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';

import { useConsentContext } from './ConsentContext';
import {
  CART_ENTRIES,
  CART_MUTATED,
  HYBRIS_CART_STORAGE_VALUE,
} from '../../constants/localStorageKeys';
import { fetchCartData } from '../../graphql/queries';
import { storageAvailable } from '../../helpers';
import { useAppContext } from '../static/AppContext';
import type { GlobalCart2 } from '~/amplienceTypes/schemas/imported/global-cart-schema';
import { useApolloContext } from '~/campaign/core/context/ApolloContext';

const ADD_TO_BAG_NOTIFICATION_TIMEOUT = 4000;

type AddToBagNotificationData = {
  product?: CartProduct | null;
};

type CartContextType = {
  miniCart: Cart;
  setMiniCart: (miniCart: Cart) => void;
  cartHovered: boolean;
  setCartHovered: (cartHovered: boolean) => void;
  addToBagNotification: AddToBagNotificationData | null;
  showAddToBagNotification: (data: AddToBagNotificationData) => void;
  hideAddToBagNotification: () => void;
  content?: GlobalCart2;
};

const CartContext = createContext<CartContextType>({
  miniCart: {},
  setMiniCart: () => {},
  cartHovered: false,
  setCartHovered: () => {},
  addToBagNotification: null,
  showAddToBagNotification: () => {},
  hideAddToBagNotification: () => {},
});

export const useCartContext = () => useContext(CartContext);

export const CartProvider: FC<PropsWithChildren<{ content?: GlobalCart2 }>> = ({
  children,
  content,
}) => {
  const { locale, country, siteType } = useAppContext();
  const { client: apolloClient } = useApolloContext();
  const { hasFullConsent } = useConsentContext();

  const [cartHovered, setCartHovered] = useState(false);
  const [miniCart, setMiniCart] = useState<Cart>({});
  const [addToBagNotification, setAddToBagNotification] = useState<AddToBagNotificationData | null>(
    null
  );
  const addToBagNotificationTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const fetchCart = async () => {
      let toFetchCart = false;

      if (typeof globalThis.window !== 'undefined') {
        const isLocalStorageAvailable = storageAvailable('localStorage');
        const cartMutated = isLocalStorageAvailable
          ? localStorage.getItem(`${country}${CART_MUTATED}`)
          : '';
        const localCartData = isLocalStorageAvailable
          ? localStorage.getItem(`${CART_ENTRIES}_${locale}`)
          : '';

        if (cartMutated === HYBRIS_CART_STORAGE_VALUE) {
          toFetchCart = true;

          if (isLocalStorageAvailable) {
            localStorage.removeItem(`${country}${CART_MUTATED}`);
          }
        } else if (localCartData) {
          setMiniCart(JSON.parse(localCartData));
        } else {
          toFetchCart = true;
        }

        if (toFetchCart) {
          const cartData = await fetchCartData({ siteType, locale, apolloClient });

          if (cartData?.cart) {
            setMiniCart(cartData.cart);

            if (isLocalStorageAvailable) {
              localStorage.setItem(`${CART_ENTRIES}_${locale}`, JSON.stringify(cartData.cart));
            }
          }
        }
      }
    };

    fetchCart();
  }, [locale, country, hasFullConsent, siteType, apolloClient]);

  const hideAddToBagNotification = useCallback(() => {
    if (addToBagNotificationTimeoutRef.current) {
      clearTimeout(addToBagNotificationTimeoutRef.current);
      addToBagNotificationTimeoutRef.current = null;
    }

    setAddToBagNotification(null);
  }, []);

  const showAddToBagNotification = useCallback(
    (data: AddToBagNotificationData) => {
      setAddToBagNotification(data);

      if (addToBagNotificationTimeoutRef.current) {
        clearTimeout(addToBagNotificationTimeoutRef.current);
        addToBagNotificationTimeoutRef.current = null;
      }

      addToBagNotificationTimeoutRef.current = window?.setTimeout(() => {
        hideAddToBagNotification();
      }, ADD_TO_BAG_NOTIFICATION_TIMEOUT);
    },
    [hideAddToBagNotification]
  );

  useEffect(() => {
    if (cartHovered && addToBagNotification) {
      hideAddToBagNotification();
    }
  }, [cartHovered, addToBagNotification, hideAddToBagNotification]);

  const value = useMemo(
    () => ({
      miniCart,
      setMiniCart,
      cartHovered,
      setCartHovered,
      addToBagNotification,
      showAddToBagNotification,
      hideAddToBagNotification,
      content,
    }),
    [
      miniCart,
      cartHovered,
      addToBagNotification,
      content,
      showAddToBagNotification,
      hideAddToBagNotification,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
